// Imports
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { PURCHASE_GIFT_CARD_ROUTE } from '../screens/PurchaseGiftCardScreenWrapper';
import { REVIEW_ORDER_SCREEN_ROUTE } from '../screens/ReviewOrderScreen';
import {
  OrderingConstants,
  OrderingOperations,
  OrderingSelectors,
  OrderingUtils,
} from 'polygon-ordering';
import constructGiftCardTopUpPaymentRequest from '../utils/constructGiftCardTopUpPaymentRequest';
import { setCheckoutInProgress } from '../slices/checkoutInProgress';
import { enqueueErrorSnackbar } from '../utils/snackbar';

import { useLocation } from 'react-router-dom';
import { GIFT_CARD_TOP_UP_ROUTE } from '../screens/GiftCardTopUp';
import { MEMBER_MONEY_ROUTE } from '../screens/MemberMoneyScreen';
import { setPaymentFormInUse } from '../slices/paymentFormInUse';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';
import getExtremelyShortScreen from '../selectors/getExtremelyShortScreen';

const { Api } = OrderingUtils;

//tyro google payment merchantId.
//the merchant ID is from google pay api console https://pay.google.com/business/console
//and all clients use the same ID for tyro
const GPAY_MERCHANTID = 'BCR2DN6T7OIZZQJW';

type PAYMENT_ROUTE_TYPES =
  | typeof REVIEW_ORDER_SCREEN_ROUTE
  | typeof PURCHASE_GIFT_CARD_ROUTE
  | typeof GIFT_CARD_TOP_UP_ROUTE
  | typeof MEMBER_MONEY_ROUTE;

export const TYRO_CONSTANTS = {
  LIBRARY: 'tyro-js-library',
  CONNECT_URI: 'https://pay.connect.tyro.com/v1/tyro.js',
  CREDIT_CARD_PAY_HTML_ID: 'tyro-pay-form-credit-card-pay',
  APPLE_GOOGLE_PAY_HTML_ID: 'tyro-pay-form-apple-google-pay',
};

const { constructSalePaymentRequest } = OrderingOperations;
const {
  getSelectedPaymentMethods,
  getMember,
  getGatewayMode,
  getPurchaserInvalidReasons,
  getOrderTotals,
  getGiftCard,
  getMerchantConfig,
} = OrderingSelectors;
const { PAYMENT_METHOD, PAYMENT_GATEWAY_MODE, TYRO_PAYMENT_TOKEN_STATUS } = OrderingConstants;

const TyroContext = createContext<any>(null);

// We are doing it like this because for gift card and top up screen method is selected from paymentMethod state and for review order screen it is selected from selectedPaymentMethods
export const getSelectedPayMethod = (
  paymentScreenRoute: PAYMENT_ROUTE_TYPES,
  paymentMethod: SubPayment | null,
  selectedPaymentMethods: SubPayment[],
) => {
  const isGiftCardOrTopUpScreen =
    paymentScreenRoute === PURCHASE_GIFT_CARD_ROUTE ||
    paymentScreenRoute === GIFT_CARD_TOP_UP_ROUTE ||
    paymentScreenRoute === MEMBER_MONEY_ROUTE;

  const isReviewOrderScreen = paymentScreenRoute === REVIEW_ORDER_SCREEN_ROUTE;

  const isMethodSelected = (method: string) => {
    if (isGiftCardOrTopUpScreen) return paymentMethod?.method === method;
    if (isReviewOrderScreen) {
      return selectedPaymentMethods.some(selected => selected.method === method);
    }
    return false;
  };

  return {
    isApplePaySelected: isMethodSelected(PAYMENT_METHOD.APPLE_PAY),
    isGooglePaySelected: isMethodSelected(PAYMENT_METHOD.GOOGLE_PAY),
    isCreditCardSelected: isMethodSelected(PAYMENT_METHOD.CREDIT_CARD),
  };
};

// Fetch pay secret function
export const fetchPaySecret = async (
  defaultSettings: any,
  dispatch: any,
  setTyroError: (err: any) => void,
  setLoading: (arg: boolean) => void,
  setFetchingPaySecret: (arg: boolean) => void,
  setPaySecretFinalised: (arg: boolean) => void,
  setConfiguration: (arg: any) => void,
  setPayRequestReady: (arg: boolean) => void,
  setPayFormReady: (arg: boolean) => void,
  resetPaySecret: () => void,
  setGiftCardTopUpData: (arg: any) => void,
  giftCardTopUpData: any,
  member: any,
  route: string | undefined,
): Promise<void> => {
  setTyroError(null);
  setLoading(true);
  setFetchingPaySecret(true);
  try {
    const authenticationMethod = member ? 'member' : 'none';

    // Helper function to construct pay secret requests
    const handleConstructPayRequest = async (
      route: string | undefined,
      authenticationMethod: 'member' | 'none',
    ) => {
      let body = null;
      let path = '';

      if (route === 'checkout') {
        const { body: checkoutBody, path: checkoutPath } = await dispatch(
          constructSalePaymentRequest({ route: 'checkout', authenticationMethod }),
        ).unwrap();
        body = checkoutBody;
        path = checkoutPath;
      }

      if (giftCardTopUpData) {
        const { body: giftCardBody, path: giftCardPath } = constructGiftCardTopUpPaymentRequest(
          giftCardTopUpData as unknown as ProcessPaymentData,
        );
        body = giftCardBody;
        path = giftCardPath;
      }

      return { body, path };
    };

    const { body: requestBody, path: requestPath } = await handleConstructPayRequest(
      route,
      authenticationMethod,
    );
    const isBodyEmpty = requestBody && Object.keys(requestBody).length === 0;

    // Checking if path has length and body is not empty
    if (requestPath.length && !isBodyEmpty) {
      const response = await Api.fetch(
        {
          method: 'POST',
          body: requestBody,
          path: requestPath,
          applicationErrorAllowed: true,
        },
        authenticationMethod,
      );

      setPaySecretFinalised(response.finalised);

      if (response.finalised) {
        setConfiguration(defaultSettings);
      } else if (response.success) {
        if (route !== 'checkout') {
          setGiftCardTopUpData({
            ...giftCardTopUpData,
            uuid: response?.uuid,
          });
        }

        setConfiguration((prevConfig: any) => ({
          ...prevConfig,
          paySecret: response.data,
        }));
      } else {
        throw new Error(response.error);
      }
    }
  } catch (error: any) {
    resetPaySecret();
    setPayRequestReady(false);
    setPayFormReady(false);
    setTyroError(error);
    enqueueErrorSnackbar(error);
  } finally {
    setLoading(false);
    setFetchingPaySecret(false);
  }
};

// Initialize Tyro.js with the Pay Request
export async function initPayRequest(
  setLoading: (arg: boolean) => void,
  setPayRequestReady: (arg: boolean) => void,
  setPayFormReady: (arg: boolean) => void,
  setSubmittingTyroForm: (arg: boolean) => void,
  gatewayMode: 'sandbox' | 'production',
  tyroJSRef: React.MutableRefObject<any>,
  paySecret: string,
  isCreditCardSelected: boolean,
): Promise<void> {
  setLoading(true);
  setPayRequestReady(false);
  setPayFormReady(false);
  setSubmittingTyroForm(false);
  try {
    // @ts-ignore
    tyroJSRef.current.tyroJSInstance = Tyro({
      liveMode: gatewayMode === PAYMENT_GATEWAY_MODE.PRODUCTION,
    });

    // Adding pay secret
    await tyroJSRef.current.tyroJSInstance.init(paySecret);
    const payFormElement = document.getElementById(
      isCreditCardSelected
        ? TYRO_CONSTANTS.CREDIT_CARD_PAY_HTML_ID
        : TYRO_CONSTANTS.APPLE_GOOGLE_PAY_HTML_ID,
    );

    if (payFormElement !== null) {
      payFormElement.innerHTML = '';
    }
    setPayRequestReady(true);
  } catch (error: any) {
    setLoading(false);
    enqueueErrorSnackbar(error?.errorMessage || `${error.toString()}`);
  }
}

// Initializing the Pay Form
export async function initPayForm(
  tyroJSRef: React.MutableRefObject<any>,
  configuration: any,
  isCreditCardSelected: boolean,
  setLoading: (arg: boolean) => void,
  setPayFormReady: (arg: boolean) => void,
  setSubmittingTyroForm: (arg: boolean) => void,
  handlePaymentProgress: (arg: boolean) => void,
  setSubmittingOverlay: (arg: boolean) => void,
  setTyroPaymentOrderId: (arg: any) => void,
  getPaymentResult: () => any,
  isApplePaySelected: boolean,
  isGooglePaySelected: boolean,
): Promise<void> {
  setLoading(true);
  setPayFormReady(false);
  setSubmittingTyroForm(false);
  try {
    const payFormElement = document.getElementById(
      isCreditCardSelected
        ? TYRO_CONSTANTS.CREDIT_CARD_PAY_HTML_ID
        : TYRO_CONSTANTS.APPLE_GOOGLE_PAY_HTML_ID,
    );

    if (!payFormElement) {
      throw new Error(`Pay Form is not mounted`);
    }
    payFormElement.innerHTML = '';

    const payForm = tyroJSRef.current.tyroJSInstance.createPayForm({
      theme: configuration.theme,
      styleProps: configuration.styleProps,
      options: configuration.options,
    });

    // Attach the Wallet Listeners to the form
    payForm.setWalletPaymentBeginListener(() => {
      handlePaymentProgress(true);
      setSubmittingOverlay(true);
    });
    payForm.setWalletPaymentCancelledListener((paymentType: any, error: any) => {
      setSubmittingOverlay(false);
      handlePaymentProgress(false);
      if (error && (paymentType === 'APPLE_PAY' || paymentType === 'GOOGLE_PAY')) {
        enqueueErrorSnackbar(error.errorMessage || error.error || `${error.toString()}`);
      }
    });
    payForm.setWalletPaymentCompleteListener(async (paymentType: any, error: any) => {
      if (error) {
        setSubmittingOverlay(false);
        handlePaymentProgress(false);
        enqueueErrorSnackbar(error.errorMessage || error.error || `${error.toString()}`);
      } else {
        if (paymentType === 'APPLE_PAY' || paymentType === 'GOOGLE_PAY') {
          const payRequest = await getPaymentResult();

          // Setting the tyro payment order id which will trigger the polling from BE to get the status and display the user based on response
          if (payRequest.status === TYRO_PAYMENT_TOKEN_STATUS.SUCCESS) {
            setTyroPaymentOrderId(payRequest.origin.orderId);
          } else {
            setSubmittingOverlay(false);
            handlePaymentProgress(false);
            enqueueErrorSnackbar(
              payRequest.errorMessage ||
                payRequest.error ||
                `${payRequest.toString()}` ||
                'Something went wrong',
            );
          }
        }
      }
    });
    payForm.setReadyListener(() => {
      setPayFormReady(true);
      setLoading(false);
    });

    const alreadyWalletButtonExists =
      (isApplePaySelected || isGooglePaySelected) &&
      document.getElementById('tyro-pay-wallet-buttons');

    // Injecting
    if (!payForm.injectStatus && !alreadyWalletButtonExists) {
      await payForm.inject(
        `#${
          isCreditCardSelected
            ? TYRO_CONSTANTS.CREDIT_CARD_PAY_HTML_ID
            : TYRO_CONSTANTS.APPLE_GOOGLE_PAY_HTML_ID
        }`,
      );
    }
  } catch {
    setLoading(false);
  }
}

export const TyroProvider = ({
  children,
  route,
}: {
  children: React.ReactNode;
  route?: string;
}) => {
  const tyroJSRef = useRef<any>(null);
  const dispatch = useAppDispatch();

  const gatewayMode = useAppSelector(getGatewayMode);
  const paymentMethod = useAppSelector(state => state.paymentMethod);
  const selectedPaymentMethods = useAppSelector(getSelectedPaymentMethods);
  const purchaserInvalidReasons = useAppSelector(getPurchaserInvalidReasons);
  const member = useAppSelector(getMember);
  const orderTotals = useAppSelector(getOrderTotals);
  const giftCard = useAppSelector(getGiftCard);
  const deviceTypeMobile = useAppSelector(getDeviceTypeMobile);
  const extremelyShortScreen = useAppSelector(getExtremelyShortScreen);
  const useMobileScreen = deviceTypeMobile || extremelyShortScreen;
  const config = useAppSelector(getMerchantConfig);
  const { merchantName } = config;

  const defaultSettings = {
    paySecret: '',
    theme: 'default',
    styleProps: {
      bodyBackgroundColor: '#fff',
      walletPaymentsDividerEnabled: false,
      walletPaymentsButtonsWidth: useMobileScreen ? '320px' : '400px',
      walletPaymentsButtonsHeight: !useMobileScreen && '50px',
    },
    options: {
      applePay: {
        enabled: false,
      },
      googlePay: {
        enabled: false,
      },
      creditCardForm: {
        enabled: false,
      },
    },
  };

  const [paymentScreenRoute, setPaymentScreenRoute] = useState<PAYMENT_ROUTE_TYPES | ''>('');

  const { isApplePaySelected, isGooglePaySelected, isCreditCardSelected } = getSelectedPayMethod(
    paymentScreenRoute as PAYMENT_ROUTE_TYPES,
    paymentMethod,
    selectedPaymentMethods,
  );

  // States
  const [configuration, setConfiguration] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [fetchingPaySecret, setFetchingPaySecret] = useState(false);
  const [payRequestReady, setPayRequestReady] = useState(false);
  const [payFormReady, setPayFormReady] = useState(false);
  const [submittingOverlay, setSubmittingOverlay] = useState(false);
  const [submittingTyroForm, setSubmittingTyroForm] = useState(false);
  const [payComplete, setPayComplete] = useState(false);
  const [showAppleGooglePayBtn, setShowAppleGooglePayBtn] = useState(false);
  // externalDependency is a state created to re-fetch the pay secret everytime it updates, so now we can use this state to regenerate pay request
  const [externalDependency, setExternalDependency] = useState(null);
  const [tyroError, setTyroError] = useState(null);
  const [paySecretFinalised, setPaySecretFinalised] = useState(false);
  const [tyroPaymentOrderId, setTyroPaymentOrderId] = useState('');
  const [giftCardTopUpData, setGiftCardTopUpData] = useState<any>(null);
  const checkoutInProgress = useAppSelector(state => state.checkoutInProgress);
  const tyroLibraryReady = useAppSelector(state => state.tyroLibraryReady);

  const { paySecret } = configuration;
  const hasPaySecret = Boolean(paySecret.length);

  const location = useLocation();

  const resetStates = () => {
    setConfiguration(defaultSettings);
    setGiftCardTopUpData(null);
    setLoading(false);
    setFetchingPaySecret(false);
    setPayRequestReady(false);
    setPayFormReady(false);
    setSubmittingOverlay(false);
    setSubmittingTyroForm(false);
    setPayComplete(false);
    setExternalDependency(null);
    setTyroError(null);
    setPaySecretFinalised(false);
    setTyroPaymentOrderId('');
  };

  useEffect(() => {
    // Setting the route name
    const path = location.pathname.replace(/^\/app/, '');
    setPaymentScreenRoute(path);
    // Resetting all states as browser location changed
    resetStates();
  }, [location]);

  // Updating config if payment method changes
  useEffect(() => {
    setConfiguration(prevConfig => ({
      ...prevConfig,
      paySecret: '',
      options: {
        applePay: { enabled: isApplePaySelected },
        googlePay: {
          enabled: isGooglePaySelected,
          merchantInfo: isGooglePaySelected
            ? {
                merchantName: merchantName,
                merchantId: GPAY_MERCHANTID,
              }
            : {},
        },
        creditCardForm: { enabled: isCreditCardSelected },
      },
    }));
  }, [isApplePaySelected, isGooglePaySelected, isCreditCardSelected]);

  // Fetching the pay secret
  useEffect(() => {
    // We generate pay secret if it's not there (!paySecret?.length) or if there is an external dependency, we use this external dependency because this helps us to trigger the re-generate of pay secrets based on user's actions
    const needsPaySecret = !paySecret?.length || externalDependency;

    // Checking whether order checkout is in progress in review order screen
    const reviewOrderScreenConditions =
      paymentScreenRoute !== REVIEW_ORDER_SCREEN_ROUTE ||
      (!checkoutInProgress && !purchaserInvalidReasons.length);

    // Checking whether any payment method is selected
    const hasOnePaymentGatewayMethodSelected =
      isApplePaySelected || isGooglePaySelected || isCreditCardSelected;

    // Checking whether we have a payment screen route set up
    const hasPaymentScreenRoute = paymentScreenRoute.length;

    // Checking whether we have one of the following either route needs to be qual to 'checkout' or we need the giftCardTopUpData
    const hasRouteOrGiftCardTopUpData = route === 'checkout' || giftCardTopUpData;

    // Checking whether order already completed
    const orderAlreadyCompleted = tyroPaymentOrderId.length;

    /* Checking whether paying with gift card or member money, user can do it with multi payment example choosing one option
    from [Google Pay, Apple Pay, Credit Card, Saved Card] and another option from  [Gift Card, Member Money]

    If paying with gift card or member money then checking whether gift card or member money balance covers the entire 
    order total if so then we don't need to generate a pay secret. This is only for checkout screen order payment not giftcard buying/topup etc.
    */
    const payMethodGiftCardOrMemberMoney = selectedPaymentMethods.find(({ method }) =>
      [PAYMENT_METHOD.GIFT_CARD, PAYMENT_METHOD.MEMBER_MONEY].includes(method),
    );

    const getPayMethodBalance = (payMethod: typeof payMethodGiftCardOrMemberMoney) => {
      if (!payMethod) {
        return 0;
      }

      if (payMethod.method === PAYMENT_METHOD.MEMBER_MONEY) {
        return member?.moneyBalance || 0;
      }

      if (payMethod.method === PAYMENT_METHOD.GIFT_CARD) {
        return giftCard?.moneyBalance || 0;
      }

      return 0;
    };

    const payMethodBalance = getPayMethodBalance(payMethodGiftCardOrMemberMoney);

    const paymentAlreadyCoveredByGiftCardOrMemberMoney =
      route === 'checkout' &&
      !!payMethodGiftCardOrMemberMoney &&
      payMethodBalance >= (orderTotals?.discountedMoneyPrice || 0);

    // For google and apple pay generating the pay secret only if showAppleGooglePayBtn is true because we want to generate the pay secret with updated data
    const walletPayButtonConditions =
      isApplePaySelected || isGooglePaySelected ? showAppleGooglePayBtn : true;

    if (
      needsPaySecret &&
      !fetchingPaySecret &&
      !paySecretFinalised &&
      reviewOrderScreenConditions &&
      hasOnePaymentGatewayMethodSelected &&
      !paymentAlreadyCoveredByGiftCardOrMemberMoney &&
      hasPaymentScreenRoute &&
      hasRouteOrGiftCardTopUpData &&
      !orderAlreadyCompleted &&
      walletPayButtonConditions
    ) {
      fetchPaySecret(
        defaultSettings,
        dispatch,
        setTyroError,
        setLoading,
        setFetchingPaySecret,
        setPaySecretFinalised,
        setConfiguration,
        setPayRequestReady,
        setPayFormReady,
        resetPaySecret,
        setGiftCardTopUpData,
        giftCardTopUpData,
        member,
        route,
      );
      setExternalDependency(null);
    }
  }, [paySecret, selectedPaymentMethods, paymentMethod, externalDependency]);

  // Function to reset the pay secret
  const resetPaySecret = () => {
    setConfiguration(prevConfig => ({
      ...prevConfig,
      paySecret: '',
    }));
  };

  // Loading the Pay Request into TyroJS
  useEffect(() => {
    if (tyroLibraryReady && paySecret?.length) {
      initPayRequest(
        setLoading,
        setPayRequestReady,
        setPayFormReady,
        setSubmittingTyroForm,
        gatewayMode,
        tyroJSRef,
        paySecret,
        isCreditCardSelected,
      );
    }
  }, [tyroLibraryReady, paySecret, configuration]);

  // Embedding the Pay Form into Document
  useEffect(() => {
    if (tyroLibraryReady && payRequestReady && !payFormReady) {
      initPayForm(
        tyroJSRef,
        configuration,
        isCreditCardSelected,
        setLoading,
        setPayFormReady,
        setSubmittingTyroForm,
        handlePaymentProgress,
        setSubmittingOverlay,
        setTyroPaymentOrderId,
        getPaymentResult,
        isApplePaySelected,
        isGooglePaySelected,
      );
    }
  }, [payRequestReady, configuration]);

  const handlePaymentProgress = (progress: boolean) => {
    if (route === 'checkout') {
      dispatch(setCheckoutInProgress(progress));
    } else if (giftCardTopUpData) {
      dispatch(setPaymentFormInUse(progress));
    }
  };

  // Handling submitting the payment
  async function submitTyroPay(): Promise<any> {
    setSubmittingTyroForm(true);
    try {
      await tyroJSRef.current.tyroJSInstance.submitPay();
      const payRequest = await getPaymentResult();
      setSubmittingTyroForm(false);
      setConfiguration(defaultSettings);

      return payRequest;
    } catch (error: any) {
      setSubmittingTyroForm(false);
      throw error;
    }
  }

  // Fetch the Pay Request Response
  async function getPaymentResult(): Promise<any> {
    const payRequest = await tyroJSRef.current.tyroJSInstance.fetchPayRequest();
    if (payRequest.status === TYRO_PAYMENT_TOKEN_STATUS.SUCCESS) {
      setPayComplete(payRequest);
    }
    setSubmittingOverlay(false);

    return payRequest;
  }

  return (
    <TyroContext.Provider
      value={{
        tyroJSRef,
        loading,
        payComplete,
        payRequestReady,
        payFormReady,
        submittingOverlay,
        submittingTyroForm,
        externalDependency,
        setExternalDependency,
        submitTyroPay,
        tyroPaymentOrderId,
        tyroError,
        setTyroPaymentOrderId,
        giftCardTopUpData,
        setGiftCardTopUpData,
        resetPaySecret,
        paySecretFinalised,
        hasPaySecret,
        showAppleGooglePayBtn,
        setShowAppleGooglePayBtn,
      }}
    >
      {children}
    </TyroContext.Provider>
  );
};

export const useTyro = () => {
  const context = useContext(TyroContext);
  if (!context) {
    throw new Error('useTyro must be used within a TyroProvider');
  }
  return context;
};
